// ==============================================
// FILE: frontend/src/components/SuperAdminLogin.js
// ==============================================

import React, { useState, useEffect } from "react";
import { Mail, Lock, ShieldCheck, Loader, AlertCircle, CheckCircle } from "lucide-react";
import { apiService } from '../../services/api.service';
import { ROUTES, STORAGE_KEYS } from '../../config/api.config';

export default function SuperAdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Check if already logged in
  useEffect(() => {
    if (apiService.isAuthenticated()) {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      if (userData) {
        const user = JSON.parse(userData);
        if (user.role === 'super_admin') {
          window.location.href = ROUTES.SUPER_ADMIN_APPROVALS;
        }
      }
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await apiService.login(email, password);

      if (response.success && response.data) {
        if (response.data.user.role !== "super_admin") {
          setError("Access denied. Only Super Admin can log in here.");
          apiService.logout();
          setLoading(false);
          return;
        }

        localStorage.setItem(STORAGE_KEYS.IS_ADMIN_LOGGED_IN, 'true');
        setSuccess("Login successful! Redirecting...");
        
        setTimeout(() => {
          window.location.href = ROUTES.SUPER_ADMIN_APPROVALS;
        }, 1200);
      } else {
        setError(response.error || "Login failed");
      }
    } catch (err) {
      setError(err.message || "Server connection failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-purple-50 to-blue-50 p-4 relative">
      
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-72 h-72 bg-purple-400/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-5%] w-96 h-96 bg-blue-400/20 rounded-full blur-3xl animate-pulse" />
      </div>

      <div className="bg-white/80 backdrop-blur-xl max-w-md w-full p-8 rounded-2xl shadow-xl border border-white/50 relative z-10">
        
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-purple-600 text-white mb-4 shadow-lg">
            <ShieldCheck size={40} />
          </div>
          <h2 className="text-3xl font-bold text-gray-700">Super Admin Login</h2>
          <p className="text-gray-500">Restricted access</p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex gap-3">
            <AlertCircle className="text-red-600" size={20} />
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex gap-3">
            <CheckCircle className="text-green-600" size={20} />
            <p className="text-green-700 text-sm">{success}</p>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4">
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="email"
              placeholder="Super Admin Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none"
              required
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-purple-600 text-white font-bold rounded-xl shadow-lg hover:bg-purple-700 flex justify-center items-center gap-2 disabled:opacity-50 transition-colors"
          >
            {loading ? (
              <>
                <Loader className="animate-spin" size={20} />
                Logging in...
              </>
            ) : "Login"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-600">
          Regular admin?{" "}
          <a href={ROUTES.LOGIN} className="text-purple-600 font-bold hover:underline">
            Login here
          </a>
        </p>
      </div>
    </div>
  );
}